<script lang="ts" setup>
	import StringMethods from '~/utils/character'

	const props = defineProps<{
		productName: string | number
		productCode?: number
		title: string
		description: string
	}>()
	const productLabel = computed(() => String(props.productName).toLowerCase())
	const { coverPeriod, getProductsByProductName } = storeToRefs(useProductsStore())
	const { internationalNoFormat } = useCurrency()
	const { toLower } = StringMethods()
	const selectedTab = shallowRef('quarterly')
	const { coverPeriod: periods } = useUtils()
	const productList = computed(() => {
		if (productLabel.value) {
			return getProductsByProductName.value?.[productLabel.value] ?? null
		}
		return null
	})

	const { attachExternalLink } = useProductsStore()
	// Filter by cover period
	const filterByCoverPeriod = computed(() => {
		if (productList.value?.list) {
			return productList.value.list.map(item => ({
				...item,
				price: item.prices.find(prices => toLower(prices.cover_period) === toLower(selectedTab.value))
			}))
		}
		return []
	})
	const emits = defineEmits<{
		(event: 'more-benefit'): void
		(event: 'other-product'): void
	}>()

	const getOriginalPrice = (discount_percent: number, price: number) => {
		/*
			Using the formula
			Original Price = Discounted Price / 1 − (Discount Percentage - 100)
			*/
		let percentage = discount_percent / 100
		percentage = 1 - percentage
		if (discount_percent && price) {
			return Math.floor(price / percentage)
		}
		return 0
	}
	const replaceMini = (arg: string) => String(arg).replace(/(\S+)\s(.+)/, '$1 <span class="font-normal">$2</span>')
</script>

<template>
	<div class="w-full">
		<ui-pad :ui="{ paddingBlock: 'd', margin: 'd' }" class="mx-auto">
			<div class="w-full mb-10 sm:mb-20">
				<ui-title-header
					:title="title"
					:ui="{
						size: 'sm:text-[3.25em] text-[1.5em] xs:text-[2em]',
						lineHeight: 'sm:leading-[62.4px] leading-tight xs:leading-[38.4px]'
					}"
					class="text-center mx-auto max-w-[1233px] w-full"
				/>
				<p class="text-gray-600 md:text-xl mt-8 xs:mt-4 text-center">
					{{ description }}
				</p>
			</div>
		</ui-pad>

		<div class="bg-health-500 w-full pb-12">
			<ui-pad :ui="{ margin: 'd', paddingInline: 'px-3', paddingBlock: 'py-8' }" class="mx-auto">
				<client-only>
					<ui-tab-group
						:list="coverPeriod"
						v-model:value="selectedTab"
						:ui="{
							tabProps: {
								wrapper: ' flex-none max-w-[500px] mx-auto',
								list: {
									base: 'w-full  tracking-wider rounded-[48px] border  border-health-500 bg-health-50',
									height: '',
									rounded: 'rounded-[20px]',
									marker: {
										base: 'min-h-[40px]',
										rounded: 'rounded-[20px]',
										background: 'bg-health-500'
									},
									tab: {
										height: 'min-h-[40px]',
										active: 'text-white font-semibold',
										font: 'font-medium capitalize ',
										shadow: ''
									}
								}
							}
						}"
					/>
				</client-only>
			</ui-pad>

			<ui-pad :ui="{ paddingInline: 'px-2', paddingBlock: 'sm:py-5 pb-10' }">
				<ui-carousel
					v-if="Array.isArray(filterByCoverPeriod)"
					class="pt-3"
					:ui="{ wrapper: { default: 'xl:justify-center' } }"
				>
					<ui-pricing-card
						v-for="(item, index) in filterByCoverPeriod"
						:key="index"
						:benefit_highlight="item.pricingBenefits?.dedicated_benfit ?? []"
						:name="{
							text: replaceMini(item.alternate_name),
							ui: {
								text: 'text-health-500',
								hover: '',
								size: 'sm:text-xl text-base',
								base: ' '
							}
						}"
						:description="{
							text: internationalNoFormat(item.price?.market_price ?? '0', '₦'),
							ui: { font: 'font-semibold', size: 'sm:text-[3em] text-[2em]' }
						}"
						:ui="{
							wrapper: {
								height: '',
								hover: '',
								padding: 'px-5 py-5 sm:py-0',
								bg: 'bg-[#F2F9FD]',
								width: 'w-full max-w-[320px] md:max-w-[440px]'
							},
							actionBtn: {
								base: 'w-full h-[56px] md:mb-8  active:scale-105 rounded-[40px] custom-transition-3 bg-transparent border-[0.63px] !text-gray-500 border-gray-400 md:bg-health-500 md:!text-white',
								display: '',
								padding: '',
								hover: 'md:hover:bg-health-600'
							},
							heading: { border: '', spacer: 'space-y-1 capitalize', description: { size: '' } },
							list: {
								base: 'border-b  md:mb-10  space-y-3 pb-3 relative z-10 md:min-h-[296px]',
								iconBase: 'fill-health-500',
								iconHover: 'fill-bg-health-500',
								text: 'text-base text-gray-500 font-medium'
							}
						}"
						class="flex-none snap-center"
						no-icon
					>
						<template #description>
							<span
								v-if="item?.price?.discount_percentage"
								class="line-through text-gray-400 text-lg"
								aria-label="discounted price"
								role="complementary"
							>
								{{
									internationalNoFormat(
										getOriginalPrice(
											item?.price?.discount_percentage ?? 0,
											Number(item.price?.market_price ?? 0)
										) * periods(selectedTab),
										'₦'
									)
								}}
							</span>
							<div class="flex items-center gap-[12px]">
								<div class="lg:text-[3.25rem]/[4rem] text-[2rem]/[2.5rem] text-blue-950">
									{{
										internationalNoFormat(
											Number(item.price?.market_price) * periods(selectedTab),
											'₦'
										)
									}}
								</div>
								<div
									class="sm:py-2.5 sm:px-4 py-1.5 px-2 rounded-[18px] sm:rounded-[20px] justify-center items-center bg-travel-100 text-travel-500 font-medium text-xs gap-1"
									:class="{
										hidden: !item?.price?.discount_percentage,
										'inline-flex': !!item?.price?.discount_percentage
									}"
								>
									Save
									<span class="font-semibold"> {{ item?.price?.discount_percentage ?? 0 }}%</span>
								</div>
							</div>
						</template>
						<template #label> / person {{ selectedTab }} </template>
						<template #btn-group="{ btnGroupClass }">
							<nuxt-link
								target="_blank"
								rel="noopener noreferrer"
								:class="[btnGroupClass, 'flex text-center justify-center font-medium']"
								external
								:to="attachExternalLink('business', `/register/${item.id}`)"
							>
								Get covered
							</nuxt-link>
						</template>
					</ui-pricing-card>
				</ui-carousel>
				<div class="flexed w-full flex-col mt-14 gap-y-4 justify-center">
					<ui-btn
						:ui="{
							btn: {
								base: 'default-btn gap-x-1 justify-center h-[56px] bg-white text-health-500 font-medium p-[12px_20px]',
								width: ' max-w-[338px] w-full',
								onHoverPseudo: 'hover:before:bg-white'
							},
							icon: { opacity: 'opacity-100' },
							text: { margin: 'group-hover:text-health-700 custom-transition-2' }
						}"
						@click="emits('more-benefit')"
					>
						<template #icon>
							<tools-icon
								icon="note-sm"
								dir="random"
								path-class="group-hover:fill-health-700 custom-transition-2"
							/>
						</template>
						<span> View more benefits </span>
					</ui-btn>
				</div>
			</ui-pad>
		</div>
	</div>
</template>
